import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useApiQuery } from '../useApiQuery';
import { useApiMutation } from '../useApiMutation';
import { operationsClient, apiGet, apiPatch, apiPost } from '@/lib/api';
import { jobEndpoints, schedulerEndpoints } from '@/lib/api/endpoints';
import { bookingKeys, jobKeys, schedulerKeys } from '@/lib/queryKeys';
import type {
  CreateRouteMembershipDto,
  CreateRouteMembershipResult,
  InsertionCandidatesResponse,
  RecurringRoutePlanResponse,
  MoveRouteMembershipDto,
  MoveRouteMembershipResult,
} from '@/types/scheduler';
import type { CreateJobDto } from '@/types/job';

/**
 * Scheduling v2 phase 4 — ranked candidate route-days for moving a recurring
 * job's visits (sorted best-first server-side; the current assignment is
 * already excluded). Fetched on demand — the route-move dialog passes
 * `enabled: open` so the query only runs while the dialog is up. Errors render
 * inline in the dialog, so the global toast is suppressed.
 */
export function useInsertionCandidates(
  jobId: string | null | undefined,
  opts: { enabled?: boolean } = {},
) {
  return useApiQuery<InsertionCandidatesResponse>({
    queryKey: schedulerKeys.insertionCandidates(jobId ?? 'none'),
    queryFn: ({ signal }) =>
      apiGet<InsertionCandidatesResponse>(
        operationsClient,
        schedulerEndpoints.routeInsertionCandidates(jobId!),
        { signal },
      ),
    enabled: (opts.enabled ?? true) && Boolean(jobId),
    showErrorToast: false,
  });
}

/**
 * §9 — create-form variant: ranked route-day candidates for a NOT-yet-created
 * recurring job, keyed by the typed service address (the backend geocodes it).
 * Runs when a location plus either serviceType or jobTypeId are present. Errors render inline (no toast).
 */
export function useInsertionCandidatesForNewJob(
  params: {
    serviceType?: string;
    jobTypeId?: string;
    address?: string;
    zipCode?: string;
    lat?: number;
    lng?: number;
    durationMinutes?: number;
    weekday?: number;
  },
  opts: { enabled?: boolean } = {},
) {
  const hasCoords = Number.isFinite(params.lat) && Number.isFinite(params.lng);
  const hasLocation = hasCoords || Boolean(params.address || params.zipCode);
  const ready = Boolean(hasLocation && (params.serviceType || params.jobTypeId));
  return useApiQuery<InsertionCandidatesResponse>({
    queryKey: schedulerKeys.insertionCandidatesByAddress(JSON.stringify(params)),
    queryFn: ({ signal }) =>
      apiGet<InsertionCandidatesResponse>(
        operationsClient,
        schedulerEndpoints.routeInsertionCandidatesByAddress({ ...params, limit: 50 }),
        { signal },
      ),
    enabled: (opts.enabled ?? true) && ready,
    showErrorToast: false,
  });
}

export function useRecurringRoutePlan(
  payload: {
    recurringConfig: NonNullable<CreateJobDto['recurringConfig']>;
    jobTypeId: string;
    serviceAddress: {
      street: string;
      street2?: string;
      city: string;
      state: string;
      zip: string;
      country?: string;
    };
  } | null,
  opts: { enabled?: boolean } = {},
) {
  const key = JSON.stringify(payload);
  return useApiQuery<RecurringRoutePlanResponse>({
    queryKey: schedulerKeys.recurringRoutePlan(key),
    queryFn: ({ signal }) =>
      apiPost<RecurringRoutePlanResponse, NonNullable<typeof payload>>(
        operationsClient,
        jobEndpoints.recurringRoutePlan(),
        payload!,
        { signal },
      ),
    enabled: (opts.enabled ?? true) && Boolean(payload),
    showErrorToast: false,
  });
}

/**
 * Shared post-mutation invalidation for route-membership writes (create/move).
 * Invalidate the whole scheduler namespace, not just `bookings()` (no params):
 * the board/calendar fetch under `bookings({ dateFrom, … })` and the param-less
 * key doesn't prefix-match that (mirrors useApplyRoute). Re-planned visits move
 * across days, so every scheduling surface — including the fetched insertion
 * candidates — must refetch. The job detail (it carries `routeMembership`) and
 * its visits list refresh too, so the recurring job page reflects the new
 * assignment immediately.
 */
function invalidateAfterMembershipWrite(queryClient: QueryClient, jobId: string) {
  void queryClient.invalidateQueries({ queryKey: schedulerKeys.all() });
  void queryClient.invalidateQueries({ queryKey: bookingKeys.all() });
  void queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) });
}

/**
 * Scheduling v2 phase 4 — move a job onto another route day (tech and/or ISO
 * weekday). The backend cancels the job's future visits and regenerates them
 * on the new day. Error toast (API message) comes from the hook default; the
 * caller owns the success toast (it needs the response counts).
 */
export function useMoveRouteMembership() {
  const queryClient = useQueryClient();
  return useApiMutation<MoveRouteMembershipResult, { jobId: string; dto: MoveRouteMembershipDto }>({
    mutationFn: ({ jobId, dto }) =>
      apiPatch<MoveRouteMembershipResult, MoveRouteMembershipDto>(
        operationsClient,
        schedulerEndpoints.routeMembership(jobId),
        dto,
      ),
    showErrorToast: true,
    onSuccess: (_res, { jobId }) => invalidateAfterMembershipWrite(queryClient, jobId),
  });
}

/**
 * Scheduling v2 §6 — initial route assignment: put an EXISTING recurring job
 * ON a route (standing technician + ISO weekday) for the first time. The
 * backend retires the prior generation's future one-off TIMED visits and
 * regenerates the lookahead window day-bound. A job that already has a
 * membership gets a 409 — use the move instead. Error toast (API message)
 * comes from the hook default; the caller owns the success toast (it needs
 * the response counts).
 */
export function useCreateRouteMembership() {
  const queryClient = useQueryClient();
  return useApiMutation<
    CreateRouteMembershipResult,
    { jobId: string; dto: CreateRouteMembershipDto }
  >({
    mutationFn: ({ jobId, dto }) =>
      apiPost<CreateRouteMembershipResult, CreateRouteMembershipDto>(
        operationsClient,
        schedulerEndpoints.routeMembership(jobId),
        dto,
      ),
    showErrorToast: true,
    onSuccess: (_res, { jobId }) => invalidateAfterMembershipWrite(queryClient, jobId),
  });
}
